<template>
  <section id="works" class="works-soon" @mousemove="handleMouseMove">
    <!-- Parallax Background Grid -->
    <div class="works-soon__grid-container">
      <div class="works-soon__grid" ref="gridRef">
        <div
          v-for="(item, index) in gridItems"
          :key="index"
          ref="cardRefs"
          class="works-card"
          :class="item.className"
        >
          <img :src="item.image" :alt="item.alt" class="works-card__image" loading="lazy" />
          <div class="works-card__overlay"></div>
        </div>
      </div>
    </div>

    <!-- Fade Overlays -->
    <div class="works-soon__fade-top"></div>
    <div class="works-soon__fade-bottom"></div>

    <!-- Main Content Overlay -->
    <div class="works-soon__container">
      <div class="works-soon__content">
        <div class="works-soon__badge" ref="badgeRef">
          <span class="works-soon__badge-dot"></span>
          {{ $t('page.worksComingSoon.badge') }}
        </div>

        <h2 class="works-soon__title" ref="titleRef" v-html="$t('page.worksComingSoon.title')"></h2>

        <p class="works-soon__description" ref="descRef">
          {{ $t('page.worksComingSoon.description') }}
        </p>

        <div class="works-soon__progress-wrapper" ref="progressRef">
          <div class="works-soon__progress-bar">
            <div class="works-soon__progress-fill"></div>
          </div>
        </div>
      </div>
    </div>

    <TiltIndicator :show="showTiltIndicator" />
  </section>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import gsap from 'gsap'
import TiltIndicator from '@/components/common/TiltIndicator.vue'
import { useDevice } from '@/composables/useDevice'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const gridRef = ref(null)
const cardRefs = ref([])
// Store mouse position for the loop to use
const mousePos = ref({ x: -9999, y: -9999 })
let scrollRaf = null

import workImage1 from '@/assets/images/works/works-card__image-1.webp'
import workImage2 from '@/assets/images/works/works-card__image-2.webp'
import workImage3 from '@/assets/images/works/works-card__image-3.webp'
import workImage4 from '@/assets/images/works/works-card__image-4.webp'
import workImage5 from '@/assets/images/works/works-card__image-5.png'
import workImage6 from '@/assets/images/works/works-card__image-6.webp'
import workImage7 from '@/assets/images/works/works-card__image-7.png'
import workImage8 from '@/assets/images/works/works-card__image-8.webp'
import workImage9 from '@/assets/images/works/works-card__image-9.png'
import workImage10 from '@/assets/images/works/works-card__image-10.webp'
import workImage11 from '@/assets/images/works/works-card__image-11.png'
import workImage12 from '@/assets/images/works/works-card__image-12.png'
import workImage13 from '@/assets/images/works/works-card__image-13.png'
import workImage14 from '@/assets/images/works/works-card__image-14.webp'
import workImage15 from '@/assets/images/works/works-card__image-15.png'

// Bento Grid Items (mix of spans)
const gridItems = [
  {
    image: workImage1,
    alt: 'Coding',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage2,
    alt: 'Abstract',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage7,
    alt: 'App',
    className: 'col-span-2 row-span-2',
  },
  {
    image: workImage4,
    alt: 'Design',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage5,
    alt: 'Mockup',
    className: 'col-span-1 row-span-2',
  },
  {
    image: workImage13,
    alt: 'Web',
    className: 'col-span-2 row-span-1',
  },
  {
    image: workImage3,
    alt: 'Space',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage15,
    alt: 'Fluid',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage9,
    alt: 'Gradient',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage14,
    alt: 'Dark',
    className: 'col-span-2 row-span-2',
  },
  {
    image: workImage11,
    alt: 'Security',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage12,
    alt: 'Mobile',
    className: 'col-span-1 row-span-2',
  },
  {
    image: workImage6,
    alt: 'Clean',
    className: 'col-span-2 row-span-1',
  },
  // Additional items
  {
    image: workImage10,
    alt: 'UX',
    className: 'col-span-1 row-span-1',
  },
  {
    image: workImage8,
    alt: 'UX',
    className: 'col-span-1 row-span-1',
  },
]

const handleMouseMove = (e) => {
  // Update stored mouse pos for the animation loop
  mousePos.value = { x: e.clientX, y: e.clientY }

  if (!gridRef.value) return
  const { innerWidth, innerHeight } = window
  // 1. Overall Parallax (Container Move - immediate feedback is fine or we can smooth it in loop too)
  // Let's keep parallax here for direct feel
  const xPos = (e.clientX / innerWidth - 0.5) * 2
  const yPos = (e.clientY / innerHeight - 0.5) * 2

  gsap.to(gridRef.value, {
    x: -xPos * 40,
    y: -yPos * 40,
    rotateX: -yPos * 5,
    rotateY: xPos * 5,
    duration: 1.2,
    ease: 'power2.out',
  })
}

// Main Animation Loop: Handles both Scroll Entrance/Exit AND Proximity Hover
const updateAnimations = () => {
  if (!cardRefs.value.length || !gridRef.value) return

  const windowHeight = window.innerHeight
  // Scroll Animation Zones - Widened Central Range
  const enterStart = windowHeight * 0.9 // Starts entering
  const enterEnd = windowHeight * 0.85 // Fully visible sooner (85% of screen height)
  const exitStart = windowHeight * 0.15 // Starts exiting later (15% of screen height)
  const exitEnd = -100 // Fully gone above

  cardRefs.value.forEach((card) => {
    if (!card) return
    const rect = card.getBoundingClientRect()

    // --- 1. Scroll Logic ---
    let scrollScale = 1
    let opacity = 1

    if (rect.top > enterEnd) {
      // Entering from bottom
      const progress = Math.min(1, Math.max(0, (enterStart - rect.top) / (enterStart - enterEnd)))
      opacity = progress
      scrollScale = 0.85 + progress * 0.15 // 0.85 -> 1.0
    } else if (rect.top < exitStart) {
      // Exiting to top
      const progress = Math.min(1, Math.max(0, (exitStart - rect.top) / (exitStart - exitEnd)))
      opacity = 1 - progress
      scrollScale = 1 - progress * 0.15 // 1.0 -> 0.85
    }

    // --- 2. Hover Proximity Logic ---
    let hoverScale = 0
    let brightness = 0.5

    // Only calc proximity if mouse has moved
    if (mousePos.value.x !== -9999) {
      const cardCenterX = rect.left + rect.width / 2
      const cardCenterY = rect.top + rect.height / 2
      const dist = Math.hypot(mousePos.value.x - cardCenterX, mousePos.value.y - cardCenterY)
      const maxDist = 400

      if (dist < maxDist) {
        const effectStrength = (maxDist - dist) / maxDist
        hoverScale = effectStrength * 0.15 // Add up to +0.15 scale
        brightness = 0.5 + effectStrength * 0.5
      }
    }

    // --- 3. Combine & Apply ---
    // Final scale = Scroll Scale + Hover Boost
    // This allows a card to be small because of scroll, but boosted by hover, or big by scroll and boosted more.
    const finalScale = scrollScale + hoverScale

    // Use GSAP setter for performance (or style direct, but gsap ensures prefixing)
    // We use .set because this runs every frame
    gsap.set(card, {
      opacity: opacity,
      transform: `scale(${finalScale})`, // Use transform string to avoid matrix math issues in some browsers
      // filter: `grayscale(100%) sepia(20%) hue-rotate(240deg) brightness(${brightness})`,
      overwrite: 'auto', // Prevent conflict with previous tweens
    })
  })

  scrollRaf = requestAnimationFrame(updateAnimations)
}

const { isDesktop } = useDevice()
const showTiltIndicator = ref(false)

onMounted(() => {
  // Show hint on mobile/tablet
  // Show hint on mobile/tablet
  if (!isDesktop.value) {
    ScrollTrigger.create({
      trigger: '#works',
      start: 'top center',
      end: 'bottom center',
      onEnter: () => {
        setTimeout(() => { showTiltIndicator.value = true }, 500)
      },
      onLeave: () => { showTiltIndicator.value = false },
      onEnterBack: () => { showTiltIndicator.value = true },
      onLeaveBack: () => { showTiltIndicator.value = false }
    })
  }

  // Start Loop
  scrollRaf = requestAnimationFrame(updateAnimations)

  // Animate content entry
  gsap.fromTo(
    '.works-soon__content > *',
    { y: 30, opacity: 0 },
    { y: 0, opacity: 1, duration: 1, stagger: 0.1, ease: 'power3.out', delay: 0.2 }
  )

  // Progress bar animation
  gsap.to('.works-soon__progress-fill', {
    width: '80%',
    duration: 2,
    ease: 'power2.inOut',
    delay: 0.5,
  })

  // Counter animation
  const percentageObj = { value: 0 }
  gsap.to(percentageObj, {
    value: 80,
    duration: 2,
    ease: 'power2.inOut',
    delay: 0.5,
    onUpdate: () => {
      const el = document.querySelector('.works-soon__percentage')
      if (el) el.textContent = `${Math.round(percentageObj.value)}%`
    },
  })

  // Init Device Orientation
  if (window.DeviceOrientationEvent) {
    window.addEventListener('deviceorientation', handleDeviceOrientation)
  }
})

onUnmounted(() => {
  if (scrollRaf) cancelAnimationFrame(scrollRaf)
  window.removeEventListener('deviceorientation', handleDeviceOrientation)
})

// ============================================================================
// DEVICE ORIENTATION (MOBILE)
// ============================================================================
let initialOrientation = { beta: 0, gamma: 0 }
let isOrientationCalibrated = false

const handleDeviceOrientation = (e) => {
  if (!gridRef.value) return

  // Calibrate initial position
  if (!isOrientationCalibrated) {
    initialOrientation = { beta: e.beta || 0, gamma: e.gamma || 0 }
    isOrientationCalibrated = true
    return
  }

  const beta = e.beta || 0
  const gamma = e.gamma || 0

  const deltaBeta = beta - initialOrientation.beta
  const deltaGamma = gamma - initialOrientation.gamma

  // Limit intensity
  // Desktop max: ~5 deg rotation, ~40px translation
  // Mobile: usage range typically +/- 20 deg

  const rotateX = Math.max(-10, Math.min(10, deltaBeta * 0.5)) // Up/Down
  const rotateY = Math.max(-10, Math.min(10, deltaGamma * 0.5)) // Left/Right

  gsap.to(gridRef.value, {
    x: -rotateY * 4, // Movement factor
    y: -rotateX * 4,
    rotateX: -rotateX,
    rotateY: rotateY,
    duration: 1.2,
    ease: 'power2.out',
  })
}
</script>

<style scoped>
.works-soon {
  @apply relative w-full flex items-center justify-center;
  min-height: 100vh;
  background: var(--color-deep-black);
  overflow: hidden;
  perspective: 1000px; /* For 3D tilt effect */
}

.works-soon__grid-container {
  @apply absolute inset-0 flex items-center justify-center overflow-hidden;
  /* Larger container for parallax movement space */
  width: 120%;
  height: 120%;
  left: -10%;
  top: -10%;
}

.works-soon__grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr); /* Increased columns */
  grid-auto-rows: 200px; /* Fixed row height helps structure */
  grid-auto-flow: dense; /* Fills gaps */
  gap: 2.5rem;
  width: 100%;
  max-width: 1800px; /* Wider max-width */
  transform-style: preserve-3d;
}

/* Responsive Grid */
@media (max-width: 1280px) {
  .works-soon__grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
@media (max-width: 1024px) {
  .works-soon__grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
@media (max-width: 768px) {
  .works-soon__grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
}

.works-card {
  @apply relative rounded-[2rem] overflow-hidden;
  min-height: 200px;
  border: 5px var(--color-deep-black);
}

.works-card__image {
  @apply w-full h-full object-cover;
}

.works-card__overlay {
  @apply absolute inset-0 dark:opacity-[95%] opacity-[93%];
  background: var(--color-deep-black);
}

.works-soon__container {
  @apply relative z-20 w-full h-screen px-6 flex justify-center items-center;
  backdrop-filter: blur(10px);
}

.works-soon__content {
  @apply flex flex-col items-center text-center;
}

.works-soon__badge {
  @apply inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.2);
  color: var(--color-electric-purple);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  backdrop-filter: blur(4px);
}

.works-soon__badge-dot {
  @apply w-1.5 h-1.5 rounded-full;
  background: var(--color-electric-purple);
  box-shadow: 0 0 8px var(--color-electric-purple);
  animation: pulse 2s infinite;
}

.works-soon__title {
  @apply text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight mb-6;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: var(--color-text-primary);
  line-height: 1.1;
}

.works-soon__description {
  @apply text-lg sm:text-xl max-w-2xl mb-12;
  color: var(--color-text-primary);
  line-height: 1.6;
}

.works-soon__progress-wrapper {
  @apply w-full max-w-sm;
}

.works-soon__progress-bar {
  @apply w-full h-1.5 rounded-full overflow-hidden mb-3;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--color-electric-purple);
  backdrop-filter: blur(4px);
}

.works-soon__progress-fill {
  @apply h-full w-0;
  background: var(--color-electric-purple);
  box-shadow: 0 0 20px var(--color-electric-purple);
}

.works-soon__fade-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 150px;
  background: linear-gradient(to bottom, var(--color-deep-black) 10%, transparent);
  z-index: 21;
  pointer-events: none;
}

.works-soon__fade-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 150px;
  background: linear-gradient(to top, var(--color-deep-black) 10%, transparent);
  z-index: 21;
  pointer-events: none;
}
</style>
